import { useState, useCallback, useRef } from "react"

import { useNavigate } from "react-router-dom"
import { Alert, Box, Container, Grid2 as Grid, useMediaQuery, useTheme } from "@mui/material"
import {
    MoreHoriz as MoreHorizIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { DateTime } from "luxon"

import {
    AuthLibMSALTokenAcquisitionError,
    useMSALPayload
} from "@initia-digital/auth-client-utils"
import {
    BasicLoader, CriticalOrSiteSpecificHazardListItemReadOnly, FilledButton, GenericAutosuggest,
    IconButton, PlanAndPrestartMultiLine,
    PlanListItem, PrestartListItem, Typography, useSnackbar,
    WorkAndSafetyJobActionToggleButtonGroup
} from "@initia-digital/component-library"
import { baseDefinedAndNotNullNarrowing, BasicLogger } from "@initia-digital/ts-utils"

import { useIsAdmin, useJobSearch, useFullTeamList } from "../hooks"
import { fetchOlapQuery, fetchPlanList, fetchPrestartList } from "../api/query"
import { BasicAppBarPage } from "../layouts/BasicAppBarPage"
import { patchPlan, patchPrestart } from "../api/mutate"
import { comparePlanDates, comparePrestartDates, filterPlanByDate } from "@initia-digital/data-service-mapping"
import { EmergencyDirectiveBanner } from "../legacy/EmergencyDirectiveBanner"

const logger = new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 4, logFormat: 'BROWSER' });

// Race condition test wrapper function
const createRaceConditionTestWrapper = (
) => {
    return async () => {
        // Simulate race condition: throw error on first call, succeed on second
        throw new AuthLibMSALTokenAcquisitionError('Unauthorized', 'initialization_failed');
    };
};

const RaceTestIndex = () => {
    // --------------------------------------------
    const [actionState, setActionState] = useState<'View Job' | 'New Plan'>("View Job");
    const actionStateHelperText = {
        "View Job": "Search to view plans and prestarts",
        "New Plan": "Search to create a plan"
    }

    // Race condition test state
    const [raceConditionEnabled, setRaceConditionEnabled] = useState(false);
    const callCounts = useRef(new Map<string, number>());

    const navigate = useNavigate();
    const { queueSnackbar } = useSnackbar();

    const authDetail = useMSALPayload();

    const isAdmin = useIsAdmin(authDetail, true);

    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    // DATA INTEGRATION
    const queryClient = useQueryClient();
    const searchJob = useJobSearch(authDetail);

    // Create wrapped functions for race condition testing
    const wrappedFetchPlanList = raceConditionEnabled 
        ? createRaceConditionTestWrapper()
        : fetchPlanList;
    
    const wrappedFetchPrestartList = raceConditionEnabled 
        ? createRaceConditionTestWrapper()
        : fetchPrestartList;
    
    const wrappedFetchOlapQuery = raceConditionEnabled 
        ? createRaceConditionTestWrapper()
        : fetchOlapQuery;

    // PLAN LISTS
    const { isLoading: isPrevPlanListLoading, data: prevPlanList, error: prevPlanListError, refetch: refetchPrevPlanList } = useQuery({
        queryKey: ['previousMonthPlanList'],
        queryFn: () => wrappedFetchPlanList(authDetail, {
            planMonth: DateTime.local().minus({ month: 1 }).toFormat('yyyy-MM')
        }),
        retry: 3, // Allow retries for race condition testing
        retryDelay: 1000 // 1 second delay between retries
    });
    const { isLoading: isPlanListLoading, data: planList, error: planListError, refetch: refetchPlanList } = useQuery({
        queryKey: ['currentMonthPlanList'],
        queryFn: () => wrappedFetchPlanList(authDetail, {
            planMonth: DateTime.local().toFormat('yyyy-MM')
        }),
        retry: 3,
        retryDelay: 1000
    });
    const { isLoading: isNextPlanListLoading, data: nextPlanList, error: nextPlanListError, refetch: refetchNextPlanList } = useQuery({
        queryKey: ['nextMonthPlanList'],
        queryFn: () => wrappedFetchPlanList(authDetail, {
            planMonth: DateTime.local().plus({ month: 1 }).toFormat('yyyy-MM')
        }),
        retry: 3,
        retryDelay: 1000
    });

    // PRESTART LISTS
    const { isLoading: isPrevPrestartListLoading, data: prevPrestartList, error: prevPrestartListError, refetch: refetchPrevPrestartList } = useQuery({
        queryKey: ['previousMonthPrestartList'],
        queryFn: () => wrappedFetchPrestartList(authDetail, {
            prestartDate: DateTime.local().minus({ month: 1 }).toFormat('yyyy-MM')
        }),
        retry: 3,
        retryDelay: 1000
    });
    const { isLoading: isPrestartListLoading, data: prestartList, error: prestartListError, refetch: refetchPrestartList } = useQuery({
        queryKey: ['currentMonthPrestartList'],
        queryFn: () => wrappedFetchPrestartList(authDetail, {
            prestartDate: DateTime.local().toFormat('yyyy-MM')
        }),
        retry: 3,
        retryDelay: 1000
    });
    const { isLoading: isNextPrestartListLoading, data: nextPrestartList, error: nextPrestartListError, refetch: refetchNextPrestartList } = useQuery({
        queryKey: ['nextMonthPrestartList'],
        queryFn: () => wrappedFetchPrestartList(authDetail, {
            prestartDate: DateTime.local().plus({ month: 1 }).toFormat('yyyy-MM')
        }),
        retry: 3,
        retryDelay: 1000
    });

    // TEAM LIST
    const { fullTeamListLoading, fullTeamListData } = useFullTeamList(authDetail, true);

    // OLAP MONTH SUMMARY
    const { isLoading: isOlapMonthSummaryLoading, data: olapMonthSummary, error: olapMonthSummaryError, refetch: refetchOlapMonthSummary } = useQuery({
        queryKey: ['olapMonthSummary'],
        queryFn: () => wrappedFetchOlapQuery<{ monthSummary: 'true' }>(authDetail, {
            monthSummary: 'true'
        }),
        retry: 3,
        retryDelay: 1000
    });

    // MUTATIONS
    const patchPlanMutation = useMutation({
        mutationFn: patchPlan,
        onSuccess: (data) => {
            // ---------------------
            queueSnackbar(`The plan has been updated`, 'success', 'Success', 3000, '1rem');
            queryClient.invalidateQueries({ queryKey: ['plan', data.id] });
            queryClient.invalidateQueries({ queryKey: ['job', data.jobId, 'plan'] });
            queryClient.invalidateQueries({ queryKey: ['previousMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['currentMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['nextMonthPlanList'] });
        },
        onError: (error) => {
            // ----------------------------
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                // --------------------------------------------
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error updating plan', { error });
            }
            logger.error('Error updating plan');
            queueSnackbar('Plan could not be updated', 'error', 'Error saving plan', 3000, '1rem');
        }
    });

    const patchPrestartMutation = useMutation({
        mutationFn: patchPrestart,
        onSuccess: (data) => {
            queueSnackbar(`The prestart has been updated`, 'success', 'Success', 3000, '1rem');
            queryClient.invalidateQueries({ queryKey: ['prestart', data.id] });
            queryClient.invalidateQueries({ queryKey: ['job', data.jobId, 'prestart'] });
            queryClient.invalidateQueries({ queryKey: ['previousMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['currentMonthPrestartList'] });
            queryClient.invalidateQueries({ queryKey: ['nextMonthPrestartList'] });
        },
        onError: (error) => {
            // ----------------------------
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                // --------------------------------------------
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error updating prestart', { error });
            }
            logger.error('Error updating prestart');
            queueSnackbar('Prestart could not be updated', 'error', 'Error saving prestart', 3000, '1rem');
        }
    });

    // EVENT HANDLERS
    const handleJobSelect = useCallback((job: Record<string, string | number | boolean | null | undefined>) => {
        if ('jobId' in job) {
            const { jobId } = job;
            navigate(actionState === 'New Plan' ? `/job/${jobId}/plan` : `/job/${jobId}`);
        }
    }, [actionState, navigate]);

    // Race condition test handlers
    const handleToggleRaceCondition = () => {
        setRaceConditionEnabled(!raceConditionEnabled);
        // Invalidate all queries to trigger fresh calls
        queryClient.invalidateQueries({ queryKey: ['previousMonthPlanList'] });
        queryClient.invalidateQueries({ queryKey: ['currentMonthPlanList'] });
        queryClient.invalidateQueries({ queryKey: ['nextMonthPlanList'] });
        queryClient.invalidateQueries({ queryKey: ['previousMonthPrestartList'] });
        queryClient.invalidateQueries({ queryKey: ['currentMonthPrestartList'] });
        queryClient.invalidateQueries({ queryKey: ['nextMonthPrestartList'] });
        queryClient.invalidateQueries({ queryKey: ['olapMonthSummary'] });
    };

    const handleRefetchAllQueries = async () => {
        try {
            // Refetch all queries simultaneously
            await Promise.all([
                refetchPrevPlanList(),
                refetchPlanList(),
                refetchNextPlanList(),
                refetchPrevPrestartList(),
                refetchPrestartList(),
                refetchNextPrestartList(),
                refetchOlapMonthSummary()
            ]);

            queueSnackbar('All queries refetched successfully', 'success', 'Refetch Complete', 3000);
        } catch (error) {
            logger.error('Error refetching queries', { error });
            queueSnackbar('Some queries failed to refetch', 'error', 'Refetch Error', 3000);
        }
    };

    // Check for race condition errors
    const hasRaceConditionErrors = 
        prevPlanListError || planListError || nextPlanListError ||
        prevPrestartListError || prestartListError || nextPrestartListError ||
        olapMonthSummaryError;

    return (
        <BasicAppBarPage bgColor={"white"}>
            <Container maxWidth="xl" sx={{ px: "25px" }}>
                <EmergencyDirectiveBanner />
                
                {/* Race Condition Test Controls */}
                <Box mb={3} p={2} border="1px solid #ccc" borderRadius={1} bgcolor="#f5f5f5">
                    <Typography
                        fontSize={"0.9rem"}
                        fontWeight={700}
                        color={theme.palette.primary.contrastText}
                    >
                        Race Condition Test Controls
                    </Typography>
                    <Box display="flex" gap={2} alignItems="center" mb={2}>
                        <FilledButton
                            color={raceConditionEnabled ? "error" : "primary"}
                            onClick={handleToggleRaceCondition}
                        >
                            {raceConditionEnabled ? "Disable" : "Enable"} Race Condition Test
                        </FilledButton>
                        <FilledButton
                            color="secondary"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefetchAllQueries}
                        >
                            Refetch All Queries
                        </FilledButton>
                        <Typography variant="body2">
                            Status: {raceConditionEnabled ? "ENABLED" : "DISABLED"}
                        </Typography>
                    </Box>
                    
                    {raceConditionEnabled && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography >
                                <strong>Race condition test is active!</strong> All API calls will throw AuthLibMSALTokenAcquisitionError 
                                with "Unauthorized" message on the first attempt, then succeed on the second attempt.
                            </Typography>
                        </Alert>
                    )}

                    {hasRaceConditionErrors && raceConditionEnabled && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography >
                                <strong>Expected behavior:</strong> You should see "Unauthorized" errors in the console and snackbar 
                                notifications, followed by successful data loading after retries.
                            </Typography>
                        </Alert>
                    )}

                    <Box>
                        <Typography
                            fontSize={"0.9rem"}
                            fontWeight={700}
                            color={theme.palette.primary.contrastText}
                        >
                            Call Counts:
                        </Typography>
                        {Array.from(callCounts.current.entries()).map(([key, count]) => (
                            <Typography key={key} variant="body2" color="text.secondary">
                                {key}: {count} calls
                            </Typography>
                        ))}
                    </Box>
                </Box>

                <Grid
                    container
                    columnSpacing={1.5} rowSpacing={2} alignItems="stretch"
                    mb="32px"
                >
                    <Grid
                        size={{ xs: 12, md: 6, lg: 4 }}
                        py="10px" border="1px solid lightgrey" borderRadius="5px"
                        height="auto"
                    >
                        <Box
                            display="flex" flexDirection="column" width="100%"
                            px="10px"
                        >
                            <Typography
                                variant="body1" fontSize={isSmallerThanSm ? "0.7rem" : "1rem"}
                                color={theme.palette.secondary.main} fontWeight={700}
                            >
                                Get started
                            </Typography>
                            <Box display="flex" flexDirection="column" rowGap="5px">
                                <Typography
                                    fontSize={isSmallerThanSm ? "0.7rem" : "0.75rem"}
                                    fontWeight={300}
                                    color={theme.palette.primary.contrastText}
                                >
                                    Choose an action and search for your job
                                </Typography>
                                <WorkAndSafetyJobActionToggleButtonGroup
                                    size="small"
                                    defaultValue={'View Job'}
                                    value={actionState}
                                    onChange={(value) => {
                                        // ---------------------
                                        if (!baseDefinedAndNotNullNarrowing(value)) {
                                            return;
                                        }
                                        setActionState(value as 'View Job' | 'New Plan')
                                    }}
                                />
                            </Box>
                            <Box
                                pt="15px"
                            >
                                <GenericAutosuggest
                                    label={actionStateHelperText[actionState]}
                                    placeholder={`Enter your job number`}
                                    labelKey="jobId"
                                    secondaryKeysForDisplay={["jobId"]}
                                    uniqueOptionKeyProps={["jobId"]}
                                    suggestionKeysForOptionDisplay={["jobId", "jobName"]}
                                    suggestionKeysToSearchOption={["jobId", "jobName"]}
                                    handleChange={handleJobSelect}
                                    searchSuggestion={searchJob}
                                />
                            </Box>

                            <Box
                                pt="15px"
                                pb="5px"
                            >
                                <Typography
                                    fontSize={"0.9rem"}
                                    fontWeight={700}
                                    color={theme.palette.primary.contrastText}
                                >
                                    Latest critical and site-specific hazards
                                </Typography>
                                {
                                    isOlapMonthSummaryLoading || !baseDefinedAndNotNullNarrowing(olapMonthSummary) ?
                                        <BasicLoader
                                            size={50}
                                        />
                                        :
                                        baseDefinedAndNotNullNarrowing(olapMonthSummary.currentHighlightedHazards) &&
                                            olapMonthSummary.currentHighlightedHazards.length === 0 ?
                                            <Box display="flex" flexDirection="row" mt="25px" width="100%" justifyContent="center">
                                                <Typography
                                                    fontSize={"0.8rem"}
                                                    fontWeight={300}
                                                    color={theme.palette.primary.contrastText}
                                                >
                                                    No critical or site-specific hazards
                                                </Typography>
                                            </Box>
                                            :
                                            <Box
                                                display="flex" flexDirection="column" rowGap="8px"
                                                mt="5px" py="5px"
                                            >
                                                {
                                                    olapMonthSummary.currentHighlightedHazards.map((hazard) => {
                                                        return (
                                                            <CriticalOrSiteSpecificHazardListItemReadOnly
                                                                key={hazard.id}
                                                                hazard={hazard}
                                                                actionButtons={[
                                                                    <IconButton
                                                                        key={`view-hazards-${hazard.id}`}
                                                                        size="small"
                                                                        tooltipTitle="More detail"
                                                                        onClick={() => {
                                                                            // ---------------------
                                                                            navigate(`/job/${hazard.jobId}/edit-plan/${hazard.planId}?stage=hazardAssessment`);
                                                                        }}
                                                                    >
                                                                        <MoreHorizIcon />
                                                                    </IconButton>
                                                                ]}
                                                            />
                                                        )
                                                    })
                                                }
                                            </Box>
                                }
                            </Box>
                            <Box
                                pt="5px"
                                pb="25px"
                            >
                                <Typography
                                    fontSize={"0.9rem"}
                                    fontWeight={700}
                                    color={theme.palette.primary.contrastText}
                                >
                                    Month Summary
                                </Typography>
                                {
                                    isOlapMonthSummaryLoading || !baseDefinedAndNotNullNarrowing(olapMonthSummary) ?
                                        <BasicLoader
                                            size={50}
                                        />
                                        :
                                        <Box
                                            mt="5px"
                                        >
                                            <PlanAndPrestartMultiLine
                                                planData={olapMonthSummary.planMonthCounts}
                                                prestartData={olapMonthSummary.prestartMonthCounts}
                                            />
                                        </Box>
                                }
                            </Box>
                        </Box>
                    </Grid>
                    <Grid
                        size={{ xs: 12, md: 6, lg: 4 }}
                        py="10px" border="1px solid lightgrey" borderRadius="5px"
                        height="auto"
                    >
                        <Box display="flex" flexDirection="column" width="100%">
                            <Box
                                display="flex" flexDirection="column"
                                px="10px"
                            >
                                <Typography
                                    variant="body1" fontSize={isSmallerThanSm ? "0.7rem" : "1rem"}
                                    color={theme.palette.secondary.main} fontWeight={700}
                                >
                                    Plans
                                </Typography>
                                <Typography
                                    fontSize={isSmallerThanSm ? "0.7rem" : "0.75rem"}
                                    fontWeight={300}
                                    color={theme.palette.primary.contrastText}
                                >
                                    Choose an upcoming plan below or go to your job using the search
                                </Typography>
                                {
                                    fullTeamListLoading || isPrevPlanListLoading || isPlanListLoading || isNextPlanListLoading
                                        || !baseDefinedAndNotNullNarrowing(fullTeamListData)
                                        || !baseDefinedAndNotNullNarrowing(prevPlanList)
                                        || !baseDefinedAndNotNullNarrowing(planList)
                                        || !baseDefinedAndNotNullNarrowing(nextPlanList) ?
                                        <BasicLoader
                                            size={50}
                                        />
                                        :
                                        <Box
                                            display="flex" flexDirection="column" justifyContent="center"
                                            mt="10px"
                                        >
                                            {
                                                (prevPlanList.length === 0 && planList.length === 0 && nextPlanList.length === 0) ?
                                                    <Box
                                                        sx={{
                                                            py: "25px"
                                                        }}
                                                    >
                                                        <Typography
                                                            fontSize={isSmallerThanSm ? "0.8rem" : "0.9rem"}
                                                            fontWeight={700}
                                                            color={theme.palette.primary.contrastText}
                                                        >
                                                            No upcoming plans
                                                        </Typography>
                                                    </Box>
                                                    :
                                                    [...prevPlanList, ...planList, ...nextPlanList]
                                                        .filter(plan => filterPlanByDate(plan, 'in_progress_or_upcoming_plus_recently_finished'))
                                                        .sort((a, b) => comparePlanDates(a, b, 'in_progress_upcoming_other_with_priority_subsort'))
                                                        .map((plan) => {
                                                            return (
                                                                <PlanListItem
                                                                    key={plan.id}
                                                                    plan={plan}
                                                                    isAdmin={isAdmin}
                                                                    teamMemberList={fullTeamListData}
                                                                    handleStatusChange={(planId, status, notify = false) => {
                                                                        // ----------------------------------------
                                                                        patchPlanMutation.mutateAsync({
                                                                            authDetail,
                                                                            planId,
                                                                            patches: [
                                                                                {
                                                                                    "op": "replace",
                                                                                    "path": "/status",
                                                                                    "value": status
                                                                                }
                                                                            ],
                                                                            notify
                                                                        });
                                                                    }}
                                                                    handleEdit={() => navigate(`/job/${plan.jobId}/edit-plan/${plan.id}`)}
                                                                    handleView={() => navigate(`/job/${plan.jobId}/view-plan/${plan.id}`)}
                                                                    handleNewPrestart={() => navigate(`/job/${plan.jobId}/plan/${plan.id}/prestart`)}
                                                                />
                                                            )
                                                        })
                                            }
                                        </Box>
                                }
                            </Box>
                        </Box>
                    </Grid>
                    <Grid
                        size={{ xs: 12, md: 6, lg: 4 }}
                        py="10px" border="1px solid lightgrey" borderRadius="5px"
                        height="auto"
                    >
                        <Box display="flex" flexDirection="column" width="100%">
                            <Box
                                display="flex" flexDirection="column"
                                px="10px"
                            >
                                <Typography
                                    variant="body1" fontSize={isSmallerThanSm ? "0.7rem" : "1rem"}
                                    color={theme.palette.secondary.main} fontWeight={700}
                                >
                                    Prestarts/Take-5's
                                </Typography>
                                <Typography
                                    fontSize={isSmallerThanSm ? "0.7rem" : "0.75rem"}
                                    fontWeight={300}
                                    color={theme.palette.primary.contrastText}
                                >
                                    Check out the latest prestarts and Take-5's being completed on site
                                </Typography>
                                {
                                    fullTeamListLoading || isPrevPrestartListLoading || isPrestartListLoading || isNextPrestartListLoading
                                        || !baseDefinedAndNotNullNarrowing(fullTeamListData)
                                        || !baseDefinedAndNotNullNarrowing(prevPrestartList)
                                        || !baseDefinedAndNotNullNarrowing(prestartList)
                                        || !baseDefinedAndNotNullNarrowing(nextPrestartList) ?
                                        <BasicLoader
                                            size={50}
                                        />
                                        :
                                        <Box
                                            display="flex" flexDirection="column" justifyContent="center"
                                            mt="10px"
                                        >
                                            {
                                                (prevPrestartList.length === 0 && prestartList.length === 0 && nextPrestartList.length === 0) ?
                                                    <Box
                                                        sx={{
                                                            py: "25px"
                                                        }}
                                                    >
                                                        <Typography
                                                            fontSize={isSmallerThanSm ? "0.8rem" : "0.9rem"}
                                                            fontWeight={700}
                                                            color={theme.palette.primary.contrastText}
                                                        >
                                                            No recent prestarts or Take-5's
                                                        </Typography>
                                                    </Box>
                                                    :
                                                    [...prevPrestartList, ...prestartList, ...nextPrestartList]
                                                        .sort(comparePrestartDates)
                                                        .map((prestart) => {
                                                            return (
                                                                <PrestartListItem
                                                                    key={`prestartlistitem-${prestart.id}`}
                                                                    prestart={prestart}
                                                                    isAdmin={isAdmin}
                                                                    teamMemberList={fullTeamListData}
                                                                    handleStatusChange={(prestartId, status, notify = false) => {
                                                                        // ----------------------------------------
                                                                        patchPrestartMutation.mutateAsync({
                                                                            authDetail,
                                                                            prestartId,
                                                                            patches: [
                                                                                {
                                                                                    "op": "replace",
                                                                                    "path": "/status",
                                                                                    "value": status
                                                                                }
                                                                            ],
                                                                            notify
                                                                        });
                                                                    }}
                                                                    handleEdit={() => navigate(`/job/${prestart.jobId}/plan/${prestart.planId}/edit-prestart/${prestart.id}`)}
                                                                    handleView={() => navigate(`/job/${prestart.jobId}/plan/${prestart.planId}/view-prestart/${prestart.id}`)}
                                                                    handleViewPlan={() => navigate(`/job/${prestart.jobId}/view-plan/${prestart.planId}`)}
                                                                />
                                                            )
                                                        })
                                            }
                                        </Box>
                                }
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </BasicAppBarPage>
    )
}

export default RaceTestIndex;